import { BELT_DEFINITIONS, TUNING } from './definitions.js'
import { resolveBeltRoutes } from './beltPaths.js'

export default class Logistics
{
    constructor(_state)
    {
        this.state = _state
        this.belts = []
        this.groundPiles = []
        this.nextItemId = 1
    }

    restore(_save)
    {
        if(!_save)
            return
        this.nextItemId = _save.nextItemId ?? this.nextItemId
        if(Array.isArray(_save.groundPiles))
            this.groundPiles = _save.groundPiles.map((_pile) => ({ ..._pile }))
        if(Array.isArray(_save.belts))
        {
            this.belts = _save.belts
                .filter((_belt) => this.getDefinition(_belt.id))
                .map((_belt) =>
                {
                    const definition = this.getDefinition(_belt.id)
                    return {
                        id: _belt.id,
                        routes: definition.destinations.map((_destination, _index) => Array.isArray(_belt.routes?.[_index]) ? _belt.routes[_index].map((_item) => ({ ..._item })).sort((_a, _b) => _a.distance - _b.distance) : []),
                        assignments: definition.split ? [...(_belt.assignments ?? [])] : [],
                        nextSplit: _belt.nextSplit ?? 0,
                        moving: false
                    }
                })
        }
        for(const belt of this.belts)
            this.ensureGroundPiles(belt)
    }

    toJSON()
    {
        return {
            nextItemId: this.nextItemId,
            belts: this.belts.map((_belt) => ({
                id: _belt.id,
                routes: _belt.routes,
                assignments: _belt.assignments,
                nextSplit: _belt.nextSplit
            })),
            groundPiles: this.groundPiles
        }
    }

    getDefinition(_id)
    {
        return BELT_DEFINITIONS.find((_definition) => _definition.id === _id)
    }

    getRoutes(_belt)
    {
        _belt.routeCache ??= resolveBeltRoutes(this.getDefinition(_belt.id), this.state.machines)
        return _belt.routeCache
    }

    buildSite(_site)
    {
        for(const beltId of _site.beltIds ?? [])
        {
            if(this.belts.some((_belt) => _belt.id === beltId))
                continue
            const definition = this.getDefinition(beltId)
            const belt = {
                id: beltId,
                routes: definition.destinations.map(() => []),
                assignments: [],
                nextSplit: 0,
                moving: false
            }
            this.belts.push(belt)
            this.ensureGroundPiles(belt)
            this.state.trigger('beltBuilt', [belt])
        }
    }

    ensureGroundPiles(_belt)
    {
        const definition = this.getDefinition(_belt.id)
        for(const destination of definition.destinations)
        {
            if(!destination.ground)
                continue
            const existing = this.groundPiles.find((_pile) => _pile.id === destination.ground)
            if(existing)
            {
                Object.assign(existing, { resource: definition.resource, x: destination.x, y: destination.y })
                continue
            }
            const pile = {
                id: destination.ground,
                resource: definition.resource,
                x: destination.x,
                y: destination.y,
                count: 0
            }
            this.groundPiles.push(pile)
            this.state.trigger('groundPileRevealed', [pile])
        }
    }

    hasSource(_machineId)
    {
        return this.belts.some((_belt) => this.getDefinition(_belt.id).source === _machineId)
    }

    update(_delta)
    {
        for(const belt of this.belts)
        {
            belt.moving = false
            const routes = this.getRoutes(belt)
            if(routes.length !== belt.routes.length)
                continue
            for(let routeIndex = 0; routeIndex < routes.length; routeIndex++)
                this.updateRoute(belt, routes[routeIndex], routeIndex, _delta)
            this.feedBelt(belt, routes)
        }
    }

    updateRoute(_belt, _route, _routeIndex, _delta)
    {
        const items = _belt.routes[_routeIndex]
        const definition = this.getDefinition(_belt.id)
        for(let index = items.length - 1; index >= 0; index--)
        {
            const item = items[index]
            const ahead = items[index + 1]
            const maximum = ahead ? Math.max(0, ahead.distance - TUNING.beltSpacing) : _route.length
            const distance = Math.min(item.distance + TUNING.beltSpeed * _delta, maximum)
            if(distance > item.distance + 0.00001)
                _belt.moving = true
            item.distance = distance
            if(item.distance + 0.00001 < _route.length)
                continue
            if(this.deliver(_belt, _route.destination))
                items.splice(index, 1)
            else
                item.distance = _route.length
        }
        if(items.length === 0 && this.getSourceOutput(_belt) > 0)
            _belt.moving = true
        if(definition.split && items.length > 0)
            _belt.moving ||= items.some((_item) => _item.distance < _route.length)
    }

    getSource(_belt)
    {
        const definition = this.getDefinition(_belt.id)
        return this.state.machines.find((_machine) => _machine.id === definition.source)
    }

    getSourceOutput(_belt)
    {
        return this.getSource(_belt)?.output ?? 0
    }

    canInsert(_belt, _route, _routeIndex)
    {
        const items = _belt.routes[_routeIndex]
        return _route.length > 0 && (items.length === 0 || items[0].distance >= TUNING.beltSpacing - 0.0001)
    }

    insert(_belt, _routeIndex)
    {
        const source = this.getSource(_belt)
        if(!source || source.output <= 0)
            return false
        source.output--
        _belt.routes[_routeIndex].unshift({ id: this.nextItemId++, distance: 0 })
        _belt.moving = true
        this.state.trigger('machineChanged', [source])
        return true
    }

    feedBelt(_belt, _routes)
    {
        const definition = this.getDefinition(_belt.id)
        const source = this.getSource(_belt)
        if(!source || source.output <= 0)
            return
        if(!definition.split)
        {
            if(this.canInsert(_belt, _routes[0], 0))
                this.insert(_belt, 0)
            return
        }

        _belt.assignments.splice(source.output)
        while(_belt.assignments.length < source.output)
        {
            _belt.assignments.push(_belt.nextSplit)
            _belt.nextSplit = (_belt.nextSplit + 1) % _routes.length
        }
        for(let index = _belt.assignments.length - 1; index >= 0; index--)
        {
            const routeIndex = _belt.assignments[index]
            let insertionRouteIndex = routeIndex
            if(!this.canInsert(_belt, _routes[routeIndex], routeIndex))
                insertionRouteIndex = _routes.findIndex((_route, _index) => _index !== routeIndex && this.canInsert(_belt, _route, _index))
            if(insertionRouteIndex < 0)
                continue
            if(this.insert(_belt, insertionRouteIndex))
                _belt.assignments.splice(index, 1)
        }
    }

    deliver(_belt, _destination)
    {
        const definition = this.getDefinition(_belt.id)
        if(_destination.machine)
        {
            const machine = this.state.machines.find((_machine) => _machine.id === _destination.machine)
            if(!machine || (machine.inputs[_destination.input] ?? 0) >= TUNING.machineSlotCapacity)
                return false
            machine.inputs[_destination.input] = (machine.inputs[_destination.input] ?? 0) + 1
            machine.receivedInputs.add(_destination.input)
            this.state.trigger('beltInput', [{ belt: _belt, machine, resource: definition.resource, sourceId: definition.source }])
            return true
        }
        const pile = this.groundPiles.find((_pile) => _pile.id === _destination.ground)
        if(!pile || pile.count >= TUNING.groundPileCapacity)
            return false
        pile.count++
        this.state.trigger('groundPileInput', [{ belt: _belt, pile, resource: definition.resource, sourceId: definition.source }])
        return true
    }
}
